
import { useState, useEffect } from "react";
import { SidebarFileList } from "./SidebarFileList";
import { ResourceContentViewer } from "./ResourceContentViewer";
import { useQuizData } from "./useQuizData";
import { supabase } from "@/integrations/supabase/client";
import { ResourceFile } from "./types";
import { InitializeDemoQuestion } from "./InitializeDemoQuestion";
import { isQuizFile } from "./utils/fileTypeUtils";

interface AdminPDFPreviewProps {
  resourceId: string;
  topic?: string;
  company?: string;
  onComplete?: () => void;
}

export function AdminPDFPreview({ resourceId, topic = "Training", company = "Company", onComplete }: AdminPDFPreviewProps) {
  const [files, setFiles] = useState<ResourceFile[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [completedFiles, setCompletedFiles] = useState<{[key: string]: boolean}>({});
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [overallProgress, setOverallProgress] = useState(0);
  const [completedAt, setCompletedAt] = useState<string | null>(null);

  const currentFile = files[currentFileIndex];
  const { quiz, isLoadingQuiz, hasAttemptedQuizFetch, setHasAttemptedQuizFetch, fetchQuiz } = useQuizData(resourceId);

  // Only fetch quiz data when viewing a quiz file
  useEffect(() => {
    if (currentFile && isQuizFile(currentFile) && !hasAttemptedQuizFetch) {
      fetchQuiz();
      setHasAttemptedQuizFetch(true);
    }
  }, [currentFileIndex, currentFile, hasAttemptedQuizFetch, fetchQuiz, setHasAttemptedQuizFetch]);

  // Fetch resource files
  useEffect(() => {
    async function fetchResources() {
      try {
        setLoadingError(null);
        console.log('Admin: Fetching resources for:', resourceId);
        const { data: resourceFiles, error } = await supabase
          .from('resource_files')
          .select('*')
          .eq('resource_id', resourceId)
          .order('order_index');

        if (error) throw error;

        if (resourceFiles && resourceFiles.length > 0) {
          console.log('Admin: Found resources:', resourceFiles);
          setFiles(resourceFiles as ResourceFile[]);
          
          // Initialize completion status for tracking display
          const completed: {[key: string]: boolean} = {};
          resourceFiles.forEach(file => {
            completed[file.id] = false;
          });
          setCompletedFiles(completed);
        }
      } catch (error) {
        console.error('Admin: Error fetching resources:', error);
        setLoadingError('Failed to load training resources. Please try again.');
      }
    }

    if (resourceId) {
      fetchResources();
    }
  }, [resourceId]);

  const handleFileChange = (index: number) => {
    setCurrentFileIndex(index);
    setHasAttemptedQuizFetch(false);
  };

  const handleContentComplete = () => {
    if (currentFile) {
      // For admin preview, just track what's been viewed
      const updatedCompletedFiles = { ...completedFiles };
      updatedCompletedFiles[currentFile.id] = true;
      setCompletedFiles(updatedCompletedFiles);
      
      // Update progress for UI display
      const totalFiles = files.length;
      const completedCount = Object.values(updatedCompletedFiles).filter(Boolean).length;
      const progress = Math.round((completedCount / totalFiles) * 100);
      setOverallProgress(progress);
      
      if (progress === 100) {
        setCompletedAt(new Date().toISOString());
      }
      
      // Navigate to next file or call onComplete
      if (currentFileIndex < files.length - 1) {
        handleFileChange(currentFileIndex + 1);
      } else if (onComplete) {
        onComplete();
      }
    }
  };

  // In admin mode, nothing is locked - remove trainee locking logic
  console.log("AdminPDFPreview rendering");

  return (
    <div className="flex h-full">
      <InitializeDemoQuestion resourceId={resourceId} />
      
      <SidebarFileList
        files={files}
        completedFiles={completedFiles}
        currentFileIndex={currentFileIndex}
        isTrainee={false}
        isAdmin={true}
        isFileLocked={() => false} // No locking for admin
        handleFileChange={handleFileChange}
        overallProgress={overallProgress}
        completedAt={completedAt}
        resourceId={resourceId}
        topic={topic}
        company={company}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden">
        <ResourceContentViewer
          currentFile={currentFile}
          resourceId={resourceId}
          isAdmin={true}
          onContentComplete={handleContentComplete}
          loadingError={loadingError}
        />
      </div>
    </div>
  );
}
